import { useCallback, useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  MenuBook as JournalIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { PDFViewer } from "@react-pdf/renderer";

import { useAuth } from "@/features/authentication";
import { PurchaseReturnPdf } from "@/features/purchase-returns";
import {
  ExpensesSection,
  GeneralInformationSection,
  ProductVariantsSection,
} from "@/features/purchase-returns/purchase-return";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  JournalDetailsDialog,
  PageContainer,
  ResponsiveToolbarButton,
  TabLabelWithCount,
} from "@/features/ui";

export const PurchaseReturn = () => {
  const { referenceNumber } = useParams();
  const { outlet, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [purchaseReturn, setPurchaseReturn] = useState(null);
  const [error, setError] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const loadPurchaseReturn = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`purchase-returns/${referenceNumber}`, {
        params: {
          withCreator: true,
          withBuyer: true,
          withSeller: true,
          withReturnable: true,
          withPurchaseReturnProductVariants: true,
          withPurchaseReturnExpenses: true,
          withTransactionHistories: true,
        },
      });
      setPurchaseReturn(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat retur pembelian")
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadPurchaseReturn();
  }, [loadPurchaseReturn]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const toolbarContent = (
    <>
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
      <ResponsiveToolbarButton
        label="Cetak PDF"
        variant="outlined"
        startIcon={<PdfIcon />}
        onClick={() => setPdfDialogOpen(true)}
      />
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Retur Pembelian"
      breadcrumbs={[
        { title: "Retur Pembelian", path: "/procurement/purchase-returns" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection purchaseReturn={purchaseReturn} />

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, newValue) => setTabIndex(newValue)}
          >
            <Tab
              label={
                <TabLabelWithCount
                  label="Barang"
                  count={
                    purchaseReturn.purchaseReturnProductVariants?.length ?? 0
                  }
                />
              }
            />
            <Tab
              label={
                <TabLabelWithCount
                  label="Biaya"
                  count={purchaseReturn.purchaseReturnExpenses?.length ?? 0}
                />
              }
            />
          </Tabs>
          <Divider sx={{ mb: 2.5 }} />

          {tabIndex === 0 && (
            <ProductVariantsSection
              purchaseReturnProductVariants={
                purchaseReturn.purchaseReturnProductVariants
              }
              returnableType={purchaseReturn.returnableType}
            />
          )}
          {tabIndex === 1 && (
            <ExpensesSection
              purchaseReturnExpenses={purchaseReturn.purchaseReturnExpenses}
            />
          )}
        </Paper>

        {/* PDF Dialog */}
        <Dialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: { height: "90vh" },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Retur Pembelian - {referenceNumber}
            <IconButton onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%" }}>
            {purchaseReturn && user && (
              <PDFViewer width="100%" height="100%">
                <PurchaseReturnPdf
                  purchaseReturn={{
                    ...purchaseReturn,
                    purchaseReturnProductVariants: [
                      ...purchaseReturn.purchaseReturnProductVariants,
                    ].sort((a, b) => a.name.localeCompare(b.name)),
                  }}
                  user={user}
                />
              </PDFViewer>
            )}
          </DialogContent>
        </Dialog>

        {/* Journal Details Dialog */}
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="PURCHASE_RETURN"
          transactionableId={purchaseReturn?.id}
        />
      </Stack>
    </PageContainer>
  );
};
